// apps/frontend/src/hooks/use-pipeline-validation.ts
import { useMemo } from 'react';
import type { PipelineNode, PipelineEdge } from '@/pages/pipelines/types/pipeline';
import type { ValidationResult, ValidationIssue, NodeValidation, EdgeValidation } from '@/lib/validation-types';
import { PipelineValidator } from '@/lib/pipeline-validator';

interface UsePipelineValidationOptions {
  nodes: PipelineNode[];
  edges: PipelineEdge[];
}

/**
 * Hook for real-time pipeline validation
 * Automatically revalidates when nodes or edges change
 */
export function usePipelineValidation({
  nodes,
  edges,
}: UsePipelineValidationOptions): ValidationResult {
  const validator = useMemo(() => new PipelineValidator(), []);

  const validationResult = useMemo(() => {
    try {
      return validator.validate(nodes, edges);
    } catch (error) {
      console.error('Pipeline validation crashed', error);

      const fatalIssue: ValidationIssue = {
        type: 'pipeline',
        severity: 'error',
        message:
          error instanceof Error
            ? `Validation failed: ${error.message}`
            : 'Validation failed due to malformed pipeline payload.',
      };

      const fallbackResult: ValidationResult = {
        isValid: false,
        healthScore: 0,
        errors: [fatalIssue],
        warnings: [],
        suggestions: [],
        nodeValidation: new Map<string, NodeValidation>(),
        edgeValidation: new Map<string, EdgeValidation>(),
        executionOrder: null,
      };

      return fallbackResult;
    }
  }, [validator, nodes, edges]);

  return validationResult;
}
